<script setup lang="tsx">
import { ref, onBeforeUnmount, watch } from 'vue'
import Multiselect from 'vue-multiselect'
import http from '@/services/http'
import type { Response, RiskCategory } from '@/models/api-generated'
import { useI18n } from 'vue-i18n'
import type { Column } from '@/stores/column'
import { commonProps } from '@/components/filter/props'
import ClearButton from '@/components/filter/ClearButton.vue'

const options = ref<RiskCategory[]>([])
const isLoading = ref(false)
const isOpen = ref(false)
const props = defineProps(commonProps)
const model = defineModel<Column>({ required: true })
const internalModel = ref<RiskCategory[]>([])

const { t } = useI18n()

watch(internalModel, () => (model.value.filter = internalModel.value.map((x) => x.id)))

async function search(q: string) {
	isLoading.value = true

	const { data } = await http.get<Response<RiskCategory[]>>(`/api/risk/categories?q=${q}`)

	if (!q) {
		data.data?.push({
			id: 0,
			name: t('misc.nothing'),
		} as RiskCategory)
	}

	options.value = data.data ?? []
	isLoading.value = false
}

function handleOpen() {
	isOpen.value = true
	search('')
}

onBeforeUnmount(() => (model.value.filter = []))
</script>

<template>
	<Multiselect
		:multiple="true"
		:show-labels="false"
		:hide-selected="true"
		:close-on-select="false"
		:clear-on-select="false"
		:id="props.id"
		:disabled="props.disabled"
		:placeholder="model.label + '...'"
		:options="options"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="internalModel"
		label="name"
		track-by="id"
		@open="handleOpen"
		@close="isOpen = false"
		:limit="isOpen ? 100 : 3"
		@search-change="search"
	>
		<template #clear>
			<ClearButton right="2rem" v-if="internalModel.length" @click="internalModel = []"></ClearButton>
		</template>

		<template v-if="!isOpen" #limit>
			<strong class="multiselect__strong">{{ $t('misc.limit', [internalModel.length - 3]) }}</strong>
		</template>
	</Multiselect>
</template>
